import { useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import TrialSignupDialog from "@/components/TrialSignupDialog";
import { API_BASE } from "@/lib/api";

const TOKEN_KEY = "nv_admin_jwt";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 29,
    tagline: "Solo studios and side businesses.",
    features: [
      "Up to 200 follow-ups / mo",
      "Thank-yous, review requests, rebook, win-back",
      "Square Appointments sync",
      "Email support",
    ],
    highlight: false,
  },
  {
    id: "growth",
    name: "Growth",
    monthly: 79,
    tagline: "The one most teams choose.",
    features: [
      "Up to 1,500 follow-ups / mo",
      "AI-drafted templates in your voice",
      "Team members + role-based access",
      "SMS delivery (coming soon)",
      "Priority email support",
    ],
    highlight: true,
  },
  {
    id: "scale",
    name: "Scale",
    monthly: 179,
    tagline: "High-volume or growing teams.",
    features: [
      "Unlimited follow-ups",
      "Advanced templates & voice tuning",
      "Founding-customer roadmap voice",
      "Dedicated onboarding call",
      "Priority phone support",
    ],
    highlight: false,
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(true);
  const [loadingId, setLoadingId] = useState(null);
  const [trialOpen, setTrialOpen] = useState(false);

  const startCheckout = async (planId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // Not logged in — send them through the trial signup flow first
      toast.info("Start your free trial first — you can subscribe right after.");
      setTrialOpen(true);
      return;
    }
    setLoadingId(planId);
    try {
      const res = await fetch(`${API_BASE}/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: planId,
          origin_url: window.location.origin,
        }),
      });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Session expired. Please sign in again.");
        setTrialOpen(true);
        return;
      }
      if (!res.ok) throw new Error("Failed to start checkout");
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error("Couldn't reach Stripe. Try again in a moment.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" className="py-24 sm:py-32 bg-[#EFECE5]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-2xl mb-10">
          <div className="overline mb-4">Simple pricing</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#183D36]">
            Priced to pay for itself in week one.
          </h2>
          <p className="mt-4 text-lg text-[#4A4A4A]">14-day free trial on every plan. No credit card up front.</p>
        </div>

        <div className="mb-10 flex items-center gap-4">
          <span className={`text-sm ${!yearly ? "text-[#183D36] font-medium" : "text-[#4A4A4A]"}`}>Monthly</span>
          <Switch data-testid="pricing-billing-toggle" checked={yearly} onCheckedChange={setYearly} />
          <span className={`text-sm ${yearly ? "text-[#183D36] font-medium" : "text-[#4A4A4A]"}`}>Yearly</span>
          {yearly && (
            <span className="text-xs font-accent uppercase tracking-widest text-[#F4B183]">Save 20%</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PLANS.map((p) => {
            const price = yearly ? Math.round(p.monthly * 0.8) : p.monthly;
            return (
              <div key={p.id} data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-3xl p-8 sm:p-10 flex flex-col ${
                  p.highlight
                    ? "bg-[#183D36] text-[#FAF7F0] shadow-2xl md:-mt-4"
                    : "bg-white border border-[#E5E0D8] text-[#252525]"
                }`}>
                {p.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#F4B183] text-[#183D36] text-xs font-accent uppercase tracking-widest font-semibold">
                    Most loved
                  </span>
                )}
                <h3 className={`font-heading text-2xl ${p.highlight ? "text-[#FAF7F0]" : "text-[#183D36]"}`}>{p.name}</h3>
                <p className={`text-sm mt-1 ${p.highlight ? "text-[#FAF7F0]/70" : "text-[#4A4A4A]"}`}>{p.tagline}</p>
                <div className="mt-6">
                  <span className={`font-heading text-5xl ${p.highlight ? "text-[#FAF7F0]" : "text-[#183D36]"}`}>${price}</span>
                  <span className={`text-sm ml-1 ${p.highlight ? "text-[#FAF7F0]/70" : "text-[#4A4A4A]"}`}>/mo</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check size={16} className={`mt-0.5 shrink-0 ${p.highlight ? "text-[#F4B183]" : "text-[#183D36]"}`} />
                      <span className={p.highlight ? "text-[#FAF7F0]/90" : "text-[#252525]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 space-y-2">
                  <Button
                    data-testid={`pricing-checkout-${p.id}`}
                    disabled={loadingId === p.id}
                    onClick={() => startCheckout(p.id)}
                    className={`w-full rounded-full py-6 text-base font-medium ${
                      p.highlight
                        ? "bg-[#F4B183] hover:bg-[#e69e6a] text-[#183D36]"
                        : "bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
                    }`}
                  >
                    {loadingId === p.id ? "Redirecting…" : "Subscribe with Stripe"}
                  </Button>
                  <Button
                    data-testid={`pricing-trial-${p.id}`}
                    variant="ghost"
                    onClick={() => setTrialOpen(true)}
                    className={`w-full rounded-full ${p.highlight ? "text-[#FAF7F0]/80 hover:bg-white/10" : "text-[#252525] hover:bg-[#EFECE5]"}`}
                  >
                    Or start 14-day free trial
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TrialSignupDialog open={trialOpen} onOpenChange={setTrialOpen} />
    </section>
  );
}
